import React, { useState, useMemo, useEffect, useCallback  } from 'react';
import { DataGridPro } from '@mui/x-data-grid-pro';
import { useMediaQuery, useTheme, Box, Typography, Divider } from '@mui/material';
import CustomToolbar from '../common/datagrid/CustomToolbar';
import CustomLoadingOverlay from '../common/datagrid/CustomLoadingOverlay';
import { styled } from '@mui/material/styles';
import { useLocation } from 'react-router-dom'; // 경로 감시용

const ChanceList = ({
  apiRef,
  columns, // 데스크탑 원본 컬럼
  rows,
  error,
  loading,
  handleRowClick,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [pageSize, setPageSize] = useState(20);

  // 스크롤 상단 이동 함수
  const scrollToTop = () => {
    if (apiRef?.current) {
      apiRef.current.scrollToIndexes({ rowIndex: 0 });
      const scroller = document.querySelector('.MuiDataGrid-virtualScroller');
      if (scroller) scroller.scrollTo({ top: 0, behavior: 'auto' });
    }
  };

  // 세션 상태 관리
  const [gridState, setGridState] = useState(() => {
    const saved = sessionStorage.getItem('datagrid-pro-state');
    return saved ? JSON.parse(saved) : {
      filter: { items: [], quickFilterValues: [] },
      sort: [],
      pagination: { page: 0, pageSize: 20 }
    };
  });

  const saveState = (key, value) => {
    const newState = { ...gridState, [key]: value };
    setGridState(newState);
    sessionStorage.setItem('datagrid-pro-state', JSON.stringify(newState));
  };

  const location = useLocation();

  // 메뉴(경로)가 바뀌면 상태 초기화 여부 결정
  useEffect(() => {
    const lastPath = sessionStorage.getItem('last-path');
    
    // 이전 경로와 현재 경로가 다르면 세션 초기화
    if (lastPath && lastPath !== location.pathname) {
      sessionStorage.removeItem('datagrid-pro-state');
      // 초기 상태로 리셋하고 싶다면 여기서 setGridState를 호출하세요.
      setGridState({
        filter: { items: [], quickFilterValues: [] },
        sort: [],
        pagination: { page: 0, pageSize: 20 }
      });
    }

    // 현재 경로 저장
    sessionStorage.setItem('last-path', location.pathname);
  }, [location.pathname]);
  

  // 모바일 전용 카드 컬럼
  const mobileColumns = useMemo(() => [
    {
      field: 'mobile_card',
      headerName: '목록',
      flex: 1,
      renderCell: (params) => {
        const { row } = params;
        return (
          <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', whiteSpace: 'normal' }}>
            <Box sx={{ p: 2, pb: 1.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600 }}>
                {row.expDay} | {row.prjTyp === 'M' ? '유지보수' : row.prjTyp === 'ETC' ? '기타매출' : '프로젝트'}
              </Typography>
              <Typography variant="caption" sx={{ color: 'primary.main', fontWeight: 'bold' }}>
                {'row.cstStatus'}
              </Typography>
            </Box>
            <Divider sx={{ mx: 2 }} />
            <Box sx={{ p: 2 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: '#111', lineHeight: 1.3, mb: 0.5 }}>
                {row.cstNm}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500, mb: 0.5 }}>
                {row.clientCmpNm}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500, mb: 0.5 }}>
                {row.vendorCmpNm}
              </Typography>
              <Typography variant="caption" color="text.disabled">
                {row.crtDptNm} · {row.crtNm} {row.crtGradeNm}
              </Typography>
            </Box>
            <Box sx={{ bgcolor: '#fafafa', px: 2, py: 1.5, borderTop: `1px solid ${theme.palette.divider}` }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="caption" display="block" color="text.disabled" sx={{ fontSize: '0.65rem' }}>매출이익</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 'bold' }}>{row.totalProfit?.toLocaleString()}</Typography>
                </Box>
                <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />
                <Box sx={{ flex: 1, textAlign: 'center' }}>
                  <Typography variant="caption" display="block" color="text.disabled" sx={{ fontSize: '0.65rem' }}>이익률</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'secondary.main' }}>{row.totalMargin}</Typography>
                </Box>
                <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />
                <Box sx={{ flex: 1, textAlign: 'right' }}>
                  <Typography variant="caption" display="block" color="text.disabled" sx={{ fontSize: '0.65rem' }}>총매출액</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 'bold' }}>{row.totalSales?.toLocaleString()}</Typography>
                </Box>
              </Box>
            </Box>
          </Box>
        );
      },
    }
  ], [theme.palette.divider]);

  return (
    <Box sx={{ display: 'flex', height: 'calc(100vh - 320px)', width: '100%' }}>
      <DataGridPro
        apiRef={apiRef}
        rows={rows || []}
        columns={isMobile ? mobileColumns : columns}

        // [기억 기능: 페이지네이션]
        // v5는 page와 pageSize를 개별 props로 관리합니다.
        page={gridState.pagination?.page || 0}
        pageSize={gridState.pagination?.pageSize || 20}
        onPageChange={(newPage) => {
          const newPagination = { ...gridState.pagination, page: newPage };
          saveState('pagination', newPagination);
          scrollToTop();
        }}
        onPageSizeChange={(newSize) => {
          const newPagination = { ...gridState.pagination, pageSize: newSize };
          saveState('pagination', newPagination);
        }}
        rowsPerPageOptions={[5, 10, 20, 50, 100]}

        // [기억 기능: 필터 및 정렬]
        filterModel={gridState.filter}
        onFilterModelChange={(newModel) => {
          saveState('filter', newModel);
          scrollToTop();
        }}
        sortModel={gridState.sort}
        onSortModelChange={(newModel) => {
          saveState('sort', newModel);
          scrollToTop();
        }}

        // [반응형 및 스타일 설정]
        getRowHeight={() => (isMobile ? 'auto' : 35)}
        getEstimatedRowHeight={() => (isMobile ? 220 : 35)}
        headerHeight={isMobile ? 0 : 56}
        rowBuffer={isMobile ? 15 : 3}
        disableColumnMenu={isMobile}
        density={isMobile ? 'standard' : 'compact'}
        disableVirtualization={isMobile && (rows?.length || 0) < 100}

        // [기타 기능]
        pagination
        loading={loading}
        onRowClick={handleRowClick}
        components={{ 
          Toolbar: CustomToolbar, 
          LoadingOverlay: CustomLoadingOverlay 
        }}
        componentsProps={{
          toolbar: { 
            fileName: '포캐스팅_리스트',
            showQuickFilter: true,
            quickFilterProps: { debounceMs: 500 },
          },
        }}
        
        sx={{
          // [PC-모바일 조건부 CSS 분리]
          '& .MuiDataGrid-row': {
            maxHeight: isMobile ? 'none !important' : undefined,
            mb: isMobile ? 1 : 0,
            mx: isMobile ? 0 : 0,
            //border: isMobile ? `1px solid ${theme.palette.divider}` : undefined,
            //borderRadius: isMobile ? '8px' : 0,
            boxShadow: isMobile ? '0 2px 4px rgba(0,0,0,0.05)' : 'none',
            backgroundColor: isMobile ? '#fff' : 'inherit',
          },
          '& .MuiDataGrid-cell': {
            display: isMobile ? 'block' : 'flex',
            alignItems: 'center',
            maxHeight: isMobile ? 'none !important' : undefined,
            padding: isMobile ? '0 !important' : undefined,
            borderBottom: isMobile ? 'none !important' : undefined, // PC는 기본 선 유지
            whiteSpace: isMobile ? 'normal' : 'nowrap',
          },
          '& .MuiDataGrid-columnHeaders': {
            display: isMobile ? 'none' : 'flex', // PC에서만 헤더 노출
          },
          '& .MuiDataGrid-virtualScroller': {
            overflowX: isMobile ? 'hidden !important' : 'auto',
            backgroundColor: isMobile ? '#f5f5f5' : 'transparent',
            pt: isMobile ? 1.5 : 0,
          },
          // PC 화면 글자 크기 최적화 (원래 스타일)
          '& .MuiDataGrid-columnHeaderTitle': {
            fontSize: isMobile ? '0.8rem' : 'inherit',
          },
          '& .MuiDataGrid-virtualScrollerRenderZone': {
            // 행이 겹치거나 잘리는 현상 방지
            position: isMobile ? 'relative !important' : 'static',
          }
        }}
      />
    </Box>
  );
};

export default ChanceList;
